import * as React from 'react';

import { Input, type InputProps } from '../../atoms/Input';
import { cn } from '../../lib/utils';

export interface TextFieldProps extends InputProps {
  /** Error message to display below the input */
  errorMessage?: string;
  /** Label for the input */
  label?: string;
  /** Container className */
  containerClassName?: string;
  /**
   * Optional icon rendered inside the input on the trailing edge (e.g. a
   * search icon, a clear-button glyph). Pure visual adornment — wire any
   * click behavior on the icon node itself.
   */
  trailingIcon?: React.ReactNode;
}

/**
 * TextField Molecule
 *
 * A text input field with optional label, error message, and trailing icon.
 * Implements all states from the Dawn Design System:
 * - Default/Stale: Grey border, grey placeholder text
 * - Focused: Orange background (`bg-orange-100`), black border, black text
 * - Filled: Black border, black text
 * - Error: Red border with error message below
 *
 * @example
 * ```tsx
 * // Default state
 * <TextField placeholder="Field Label" />
 *
 * // With value (filled state)
 * <TextField value="Full Field" variant="filled" />
 *
 * // With error
 * <TextField
 *   value="Full Field"
 *   error
 *   errorMessage="Something went wrong! Please try again"
 * />
 *
 * // With label
 * <TextField label="Email" placeholder="Enter email" />
 *
 * // With trailing icon (e.g. search)
 * <TextField placeholder="Search" trailingIcon={<SearchIcon />} />
 * ```
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ errorMessage, label, containerClassName, className, error, trailingIcon, ...props }, ref) => {
    // Determine if we should show error styling
    const hasError = error || !!errorMessage;

    return (
      <div className={cn('flex w-full flex-col items-start', containerClassName)}>
        {label && (
          <label className="mb-1 font-outfit text-[13px] font-medium leading-[1.3] text-dawn-black">
            {label}
          </label>
        )}
        <div className="relative w-full">
          <Input
            ref={ref}
            error={hasError}
            className={cn(trailingIcon && 'pr-9', className)}
            {...props}
          />
          {trailingIcon && (
            <span
              data-testid="text-field-trailing-icon"
              className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-grey-400"
            >
              {trailingIcon}
            </span>
          )}
        </div>
        {errorMessage && (
          <p className="mt-2 font-outfit text-[10px] font-normal capitalize leading-[1.2] text-error">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
